import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "../../../configs/apiRoutes";
import instance from "../../../api/apiClient";
import sanitizeFileName from "helpers/sanitizeFileName";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import { Translation } from "../types";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";
import { normalizeSeoHtml } from "helpers/seoAttributes";
import type { RootState } from "store";

export interface CategoryItem {
  id: number;
  title: string;
  description: string;
  slug: string;
  isActive: string;
  image: string;
  icon: string;
  countOfCollections: number;
  translations: Array<Translation>;
}
export interface CategoryBunchPayload {
  ids: number[];
  isActive: number;
}
interface CategoryPayload {
  id?: number;
  title?: string;
  description?: string;
  slug?: string;
  isActive?: boolean;
  image?: string | File;
  icon?: string | File;
  translations?: Array<Translation>;
}
interface CategoriesState {
  list: CategoryItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  isItemLocaleFetching: boolean;
  selectedItems: number[];
  payload?: CategoryPayload;
  initialPayload?: CategoryPayload;
  processingItemId?: number;
}
interface GetListPayload {
  limit?: number;
  offset?: number;
  isActive?: number;
  title?: string;
  order?: {
    title: string;
    slug: string;
    countOfCollections: string;
  };
}
export const getCategoriesList = createAsyncThunk(
  "game-center/categories/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.categoriesList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const createCategory = createAsyncThunk<
  number,
  CategoryPayload,
  { rejectValue: ApiErrorPayload }
>(
  "game-center/categories/create",
  async (payload: CategoryPayload, { rejectWithValue }) => {
    const data = new FormData();

    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }

    if (payload.icon instanceof File) {
      const safeName = sanitizeFileName(payload.icon.name);

      data.append(
        "icon",
        new File([payload.icon], safeName, { type: payload.icon.type }),
      );
    }

    Object.entries(payload).forEach(([key, value]) => {
      if (key === "image" || key === "icon") return;
      if (key === "isActive") {
        data.append("isActive", value ? "1" : "0");
        return;
      }

      if (value) {
        data.append(key, (value as string) ?? "");
      }
    });

    try {
      const response = await instance.post(
        `${apiRoutes.categoryCreate}`,
        data,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data.id;
    } catch (error: any) {
      const apiError = error?.data?.error;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const updateCategory = createAsyncThunk<
  boolean,
  CategoryPayload,
  { rejectValue: ApiErrorPayload }
>(
  "game-center/categories/update",
  async (payload: CategoryPayload, { rejectWithValue }) => {
    const data = new FormData();

    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }

    if (payload.icon instanceof File) {
      const safeName = sanitizeFileName(payload.icon.name);

      data.append(
        "icon",
        new File([payload.icon], safeName, { type: payload.icon.type }),
      );
    }

    data.append("title", (payload?.title as string) ?? "");
    data.append("slug", (payload.slug as string) ?? "");
    data.append("description", (payload.description as string) ?? "");
    data.append("isActive", payload.isActive ? "1" : "0");

    try {
      await instance.put(`${apiRoutes.categoryUpdate}/${payload.id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return true;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError && apiError.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const updateCategoriesBunch = createAsyncThunk(
  "game-center/categories/update-bunch",
  async (data: CategoryBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.updateCategoriesBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const getCategoryItem = createAsyncThunk(
  "game-center/categories/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.categoryRead}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const deleteCategoryItem = createAsyncThunk(
  "game-center/categories/delete",
  async (id: string | number) => {
    try {
      const result = await instance.delete(`${apiRoutes.categoryDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const getCategoryItemLocale = createAsyncThunk(
  "game-center/categories/get-item-locale",
  async ({ id, locale }: { id: string | number; locale: string }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.categoryRead}/${id}/translation/${locale}`,
      );
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const updateCategoryItemLocale = createAsyncThunk(
  "game-center/categories/update-item-locale",
  async (
    { id, locale }: { id: string | number; locale: Partial<Translation> },
    { getState },
  ) => {
    try {
      const { locale: localeName, ...restData } = locale;
      const initialTranslation = (
        getState() as RootState
      ).categories.initialPayload?.translations?.find(
        (translation) => translation.locale === localeName,
      );
      const dataToUpdate = getChangedFields(
        initialTranslation,
        restData,
        ["title", "description", "isActive"],
        {
          title: normalizeOptionalText,
          description: normalizeSeoHtml,
          isActive: Boolean,
        },
      );

      await instance.put(
        `${apiRoutes.categoryRead}/${id}/translation/${localeName}`,
        dataToUpdate,
      );
      return true;
    } catch (error) {
      return false;
    }
  },
);

const initialState: CategoriesState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  isItemFetching: false,
  isItemLocaleFetching: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
};

export const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    handleUpdateFields: (
      state: CategoriesState,
      action: PayloadAction<CategoryPayload>,
    ) => {
      const isFirstUpdate = !state.payload;

      state.payload = {
        ...(isFirstUpdate ? { isActive: false } : {}),
        ...state.payload,
        ...action.payload,
      };
    },
    handleClearFields: (state: CategoriesState) => {
      state.payload = undefined;
      state.initialPayload = undefined;
      state.isItemFetched = false;
    },
    handlePaginate: (state: CategoriesState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectItem: (
      state: CategoriesState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: CategoriesState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCategoriesList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createCategory.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createCategory.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });
    builder.addCase(createCategory.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateCategory.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateCategory.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, {
              ...action.meta.arg,
              image:
                action.meta.arg.image instanceof File
                  ? item.image
                  : action.meta.arg.image ?? item.image,
              icon:
                action.meta.arg.icon instanceof File
                  ? item.icon
                  : action.meta.arg.icon ?? item.icon,
              isActive: String(action.meta.arg.isActive),
            })
          : item,
      );
      state.processingItemId = undefined;
    });
    builder.addCase(updateCategory.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateCategoriesBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(getCategoryItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = action.payload;
      state.initialPayload = action.payload
        ? {
            ...action.payload,
            translations: action.payload.translations?.map(
              (translation: Translation) => ({
                ...translation,
              }),
            ),
          }
        : undefined;
    });
    builder.addCase(deleteCategoryItem.pending, (state, action) => {
      state.processingItemId = +action.meta.arg;
    });
    builder.addCase(deleteCategoryItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deleteCategoryItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(getCategoryItemLocale.pending, (state) => {
      state.isItemLocaleFetching = true;
    });
    builder.addCase(getCategoryItemLocale.fulfilled, (state, action) => {
      state.isItemLocaleFetching = false;
    });
    builder.addCase(getCategoryItemLocale.rejected, (state, action) => {
      state.isItemLocaleFetching = false;
    });
    builder.addCase(updateCategoryItemLocale.pending, (state) => {
      state.isItemLocaleFetching = true;
      state.isItemFetching = true;
    });
    builder.addCase(updateCategoryItemLocale.fulfilled, (state, action) => {
      state.isItemLocaleFetching = false;
      state.isItemFetching = false;
      state.initialPayload = state.payload
        ? {
            ...state.payload,
            translations: state.payload.translations?.map((translation) => ({
              ...translation,
            })),
          }
        : undefined;
    });
    builder.addCase(updateCategoryItemLocale.rejected, (state, action) => {
      state.isItemLocaleFetching = false;
      state.isItemFetching = false;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
} = categoriesSlice.actions;
